import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  getProviderConfig,
  ProviderConnectionData,
  Address,
  BillingAddress,
} from '../lib/providerTypes';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useToast } from '../contexts/ToastContext';

interface ProviderEditModalProps {
  providerId: string;
  providerName: string;
  providerType: string;
  merchantId: string;
  authUserId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const ProviderEditModal: React.FC<ProviderEditModalProps> = ({
  providerId,
  providerName,
  providerType,
  merchantId,
  authUserId,
  onClose,
  onSuccess,
}) => {
  const config = getProviderConfig(providerType);
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Form data
  const [formData, setFormData] = useState<ProviderConnectionData>({
    stock_provider: true,
    orders_provider: true,
    store_shipping_address: {
      name: '',
      street: '',
      city: '',
      zip_code: '',
      country: '',
      phone_number: '',
      email: '',
    },
    store_billing_address: {
      company_name: '',
      vat_id: '',
      street: '',
      city: '',
      zip_code: '',
      country: '',
    },
    auto_order_handler: null,
    test_mode: false,
    local_pickup_shipping_methods: [],
    local_pickup_batch_threshold: 10,
  });

  const [localPickupMethodsInput, setLocalPickupMethodsInput] = useState('');

  useEffect(() => {
    loadExistingConnection();
  }, [providerId, merchantId]);

  const loadExistingConnection = async () => {
    setLoading(true);

    try {
      const { data, error: fetchError } = await supabase
        .from('merchant_provider_map')
        .select('stock_provider, orders_provider, capabilities')
        .eq('merchant_id', merchantId)
        .eq('provider_id', providerId)
        .single();

      if (fetchError) throw fetchError;
      if (!data) throw new Error('Connection not found');

      // Pre-fill form with existing data
      const capabilities = data.capabilities || {};
      const handlerConfig = capabilities.handler_config || {};

      setFormData({
        stock_provider: data.stock_provider ?? true,
        orders_provider: data.orders_provider ?? false,
        store_shipping_address: handlerConfig.store_shipping_address || {
          name: '',
          street: '',
          city: '',
          zip_code: '',
          country: '',
          phone_number: '',
          email: '',
        },
        store_billing_address: handlerConfig.store_billing_address || {
          company_name: '',
          vat_id: '',
          street: '',
          city: '',
          zip_code: '',
          country: '',
        },
        auto_order_handler: capabilities.auto_order_handler || null,
        test_mode: handlerConfig.test_mode ?? false,
        local_pickup_shipping_methods: handlerConfig.local_pickup_shipping_methods || [],
        local_pickup_batch_threshold: handlerConfig.local_pickup_batch_threshold ?? 10,
      });

      // Set local pickup methods input
      if (handlerConfig.local_pickup_shipping_methods) {
        setLocalPickupMethodsInput(handlerConfig.local_pickup_shipping_methods.join(', '));
      }
    } catch (err) {
      console.error('Error loading connection:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to load connection settings');
    } finally {
      setLoading(false);
    }
  };

  // Get available steps
  const getAvailableSteps = () => {
    let steps = [...config.steps];
    if (!formData.orders_provider) {
      steps = steps.filter((s) => s !== 'addresses' && s !== 'auto_order');
    }
    return steps;
  };

  const availableSteps = getAvailableSteps();
  const currentStepName = availableSteps[currentStep];
  const isLastStep = currentStep === availableSteps.length - 1;

  const updateFormData = (updates: Partial<ProviderConnectionData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const updateAddress = (field: keyof Address, value: string) => {
    setFormData((prev) => ({
      ...prev,
      store_shipping_address: {
        ...prev.store_shipping_address!,
        [field]: value,
      },
    }));
  };

  const updateBillingAddress = (field: keyof BillingAddress, value: string) => {
    setFormData((prev) => ({
      ...prev,
      store_billing_address: {
        ...prev.store_billing_address!,
        [field]: value,
      },
    }));
  };

  const validateCurrentStep = (): boolean => {
    switch (currentStepName) {
      case 'capabilities':
        if (!formData.stock_provider && !formData.orders_provider) {
          toast.error('Please select at least one capability');
          return false;
        }
        break;

      case 'addresses':
        const addr = formData.store_shipping_address!;
        if (!addr.name || !addr.street || !addr.city || !addr.zip_code || !addr.country || !addr.phone_number) {
          toast.error('Please fill all required shipping address fields');
          return false;
        }
        if (config.requiresEmail && !addr.email) {
          toast.error(`${providerName} requires an email address`);
          return false;
        }

        const billing = formData.store_billing_address!;
        if (!billing.company_name || !billing.street || !billing.city || !billing.zip_code || !billing.country) {
          toast.error('Please fill all required billing address fields');
          return false;
        }
        break;

      case 'auto_order':
        // Auto-order config is optional
        break;
    }

    return true;
  };

  const handleNext = async () => {
    if (!validateCurrentStep()) {
      return;
    }

    if (isLastStep) {
      await handleSubmit();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  };

  const handleSubmit = async () => {
    setSaving(true);

    try {
      // Parse local pickup methods
      const pickupMethods = localPickupMethodsInput
        .split(',')
        .map((m) => m.trim())
        .filter((m) => m.length > 0);

      // Build capabilities update
      const capabilities: any = {
        stock_provider: formData.stock_provider,
        orders_provider: formData.orders_provider,
      };

      if (formData.orders_provider && formData.auto_order_handler) {
        capabilities.auto_order = true;
        capabilities.auto_order_handler = formData.auto_order_handler;
        capabilities.handler_config = {
          test_mode: formData.test_mode,
          store_shipping_address: formData.store_shipping_address,
          store_billing_address: formData.store_billing_address,
          local_pickup_shipping_methods: pickupMethods,
          local_pickup_batch_threshold: formData.local_pickup_batch_threshold,
        };
      }

      // Update via Edge Function
      const { data, error: apiError } = await supabase.functions.invoke('update-provider-settings', {
        body: {
          merchant_id: merchantId,
          provider_id: providerId,
          capabilities,
        },
      });

      if (apiError) throw apiError;
      if (data?.error) throw new Error(data.error);

      onSuccess();
    } catch (err) {
      console.error('Update error:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to update provider settings');
    } finally {
      setSaving(false);
    }
  };

  // Render capabilities step
  const renderCapabilitiesStep = () => (
    <div className="space-y-4">
      <p className="text-sm text-white/50 mb-4">
        Select what this provider will be used for:
      </p>
      <label className="flex items-center space-x-3 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.stock_provider}
          onChange={(e) => updateFormData({ stock_provider: e.target.checked })}
          className="w-5 h-5 text-blue-600 border-white/15 rounded focus:ring-blue-500"
          disabled={loading || saving}
        />
        <div>
          <div className="font-medium text-white">Stock Provider</div>
          <div className="text-sm text-white/35">Sync inventory from this provider</div>
        </div>
      </label>
      <label className="flex items-center space-x-3 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.orders_provider}
          onChange={(e) => updateFormData({ orders_provider: e.target.checked })}
          className="w-5 h-5 text-blue-600 border-white/15 rounded focus:ring-blue-500"
          disabled={loading || saving}
        />
        <div>
          <div className="font-medium text-white">Orders Provider</div>
          <div className="text-sm text-white/35">Fulfill orders through this provider</div>
        </div>
      </label>
      {!formData.orders_provider && (
        <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-xl rounded text-sm text-yellow-400">
          Note: Without "Orders Provider", auto-order features will be unavailable.
        </div>
      )}
    </div>
  );

  // Render addresses step (same as ProviderConnectModal)
  const renderAddressesStep = () => (
    <div className="space-y-6">
      {/* Shipping Address */}
      <div>
        <h3 className="font-semibold text-white mb-3">Store Shipping Address *</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-white/70 mb-1">Name *</label>
            <input
              type="text"
              value={formData.store_shipping_address?.name || ''}
              onChange={(e) => updateAddress('name', e.target.value)}
              className="w-full px-3 py-2 border border-white/15 rounded-md"
              disabled={loading || saving}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-white/70 mb-1">Street *</label>
            <input
              type="text"
              value={formData.store_shipping_address?.street || ''}
              onChange={(e) => updateAddress('street', e.target.value)}
              className="w-full px-3 py-2 border border-white/15 rounded-md"
              disabled={loading || saving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">City *</label>
            <input
              type="text"
              value={formData.store_shipping_address?.city || ''}
              onChange={(e) => updateAddress('city', e.target.value)}
              className="w-full px-3 py-2 border border-white/15 rounded-md"
              disabled={loading || saving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">Zip Code *</label>
            <input
              type="text"
              value={formData.store_shipping_address?.zip_code || ''}
              onChange={(e) => updateAddress('zip_code', e.target.value)}
              className="w-full px-3 py-2 border border-white/15 rounded-md"
              disabled={loading || saving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">Country Code *</label>
            <input
              type="text"
              value={formData.store_shipping_address?.country || ''}
              onChange={(e) => updateAddress('country', e.target.value.toUpperCase())}
              className="w-full px-3 py-2 border border-white/15 rounded-md"
              placeholder="FR, NL, US, etc."
              maxLength={2}
              disabled={loading || saving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">Phone *</label>
            <input
              type="tel"
              value={formData.store_shipping_address?.phone_number || ''}
              onChange={(e) => updateAddress('phone_number', e.target.value)}
              className="w-full px-3 py-2 border border-white/15 rounded-md"
              disabled={loading || saving}
            />
          </div>
          {config.requiresEmail && (
            <div className="col-span-2">
              <label className="block text-sm font-medium text-white/70 mb-1">
                Email * <span className="text-xs text-white/35">(required by {providerName})</span>
              </label>
              <input
                type="email"
                value={formData.store_shipping_address?.email || ''}
                onChange={(e) => updateAddress('email', e.target.value)}
                className="w-full px-3 py-2 border border-white/15 rounded-md"
                disabled={loading || saving}
              />
            </div>
          )}
        </div>
      </div>

      {/* Billing Address */}
      <div>
        <h3 className="font-semibold text-white mb-3">Store Billing Address *</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-white/70 mb-1">Company Name *</label>
            <input
              type="text"
              value={formData.store_billing_address?.company_name || ''}
              onChange={(e) => updateBillingAddress('company_name', e.target.value)}
              className="w-full px-3 py-2 border border-white/15 rounded-md"
              disabled={loading || saving}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-white/70 mb-1">VAT ID (optional)</label>
            <input
              type="text"
              value={formData.store_billing_address?.vat_id || ''}
              onChange={(e) => updateBillingAddress('vat_id', e.target.value)}
              className="w-full px-3 py-2 border border-white/15 rounded-md"
              disabled={loading || saving}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-white/70 mb-1">Street *</label>
            <input
              type="text"
              value={formData.store_billing_address?.street || ''}
              onChange={(e) => updateBillingAddress('street', e.target.value)}
              className="w-full px-3 py-2 border border-white/15 rounded-md"
              disabled={loading || saving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">City *</label>
            <input
              type="text"
              value={formData.store_billing_address?.city || ''}
              onChange={(e) => updateBillingAddress('city', e.target.value)}
              className="w-full px-3 py-2 border border-white/15 rounded-md"
              disabled={loading || saving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">Zip Code *</label>
            <input
              type="text"
              value={formData.store_billing_address?.zip_code || ''}
              onChange={(e) => updateBillingAddress('zip_code', e.target.value)}
              className="w-full px-3 py-2 border border-white/15 rounded-md"
              disabled={loading || saving}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-white/70 mb-1">Country Code *</label>
            <input
              type="text"
              value={formData.store_billing_address?.country || ''}
              onChange={(e) => updateBillingAddress('country', e.target.value.toUpperCase())}
              className="w-full px-3 py-2 border border-white/15 rounded-md"
              placeholder="FR, NL, US, etc."
              maxLength={2}
              disabled={loading || saving}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Render auto-order config step (same as ProviderConnectModal)
  const renderAutoOrderStep = () => {
    const handlerOptions = [
      { value: '', label: 'None (Tag only - manual processing)' },
      ...config.supportedHandlers.map((h) => ({
        value: h,
        label: h.charAt(0).toUpperCase() + h.slice(1),
      })),
    ];

    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-white/70 mb-2">
            Auto-Order Handler
          </label>
          <select
            value={formData.auto_order_handler || ''}
            onChange={(e) => updateFormData({ auto_order_handler: e.target.value || null })}
            className="w-full px-3 py-2 border border-white/15 rounded-md "
            disabled={loading || saving}
          >
            {handlerOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.test_mode}
            onChange={(e) => updateFormData({ test_mode: e.target.checked })}
            className="w-5 h-5 text-blue-600 border-white/15 rounded"
            disabled={loading || saving}
          />
          <div>
            <div className="font-medium text-white">Test Mode</div>
            <div className="text-sm text-white/35">Simulate orders without actually placing them</div>
          </div>
        </label>

        <div>
          <label className="block text-sm font-medium text-white/70 mb-2">
            Local Pickup Shipping Methods
          </label>
          <input
            type="text"
            value={localPickupMethodsInput}
            onChange={(e) => setLocalPickupMethodsInput(e.target.value)}
            className="w-full px-3 py-2 border border-white/15 rounded-md"
            placeholder="Afhalen in winkel, Store pickup"
            disabled={loading || saving}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-white/70 mb-2">
            Batch Threshold
          </label>
          <input
            type="number"
            value={formData.local_pickup_batch_threshold}
            onChange={(e) => updateFormData({ local_pickup_batch_threshold: parseInt(e.target.value) || 10 })}
            className="w-full px-3 py-2 border border-white/15 rounded-md"
            min="1"
            disabled={loading || saving}
          />
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStepName) {
      case 'capabilities':
        return renderCapabilitiesStep();
      case 'addresses':
        return renderAddressesStep();
      case 'auto_order':
        return renderAutoOrderStep();
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStepName) {
      case 'capabilities':
        return 'Select Capabilities';
      case 'addresses':
        return 'Store Addresses';
      case 'auto_order':
        return 'Auto-Order Configuration';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="modal-body rounded-2xl p-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <p className="mt-4 text-white/50 ">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="modal-body rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/[0.08]">
          <div>
            <h2 className="text-2xl font-bold text-white">
              Edit {providerName} Settings
            </h2>
            <p className="text-sm text-white/50 mt-1">
              Step {currentStep + 1} of {availableSteps.length}: {getStepTitle()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/25 hover:text-white/50"
            disabled={saving}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="w-full bg-white/10 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / availableSteps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {renderCurrentStep()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-white/[0.08] bg-transparent">
          <button
            onClick={handleBack}
            disabled={currentStep === 0 || saving}
            className="px-4 py-2 text-white/70  border border-white/15 rounded-md hover:bg-white/[0.08] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Back
          </button>
          <button
            onClick={handleNext}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : isLastStep ? 'Save Settings' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProviderEditModal;
